// ============================================
// src/PaymentSystem.js
// Unified payment system supporting BOTH Flutterwave and Paystack
// User picks which one they want to pay with
// ============================================
import React from 'react';
import { CreditCard, Check, X } from 'lucide-react';
import { SUBSCRIPTION_PLANS } from './SubscriptionSystem';
import { initiateFlutterwavePayment } from './FlutterwavePayment';
import { initiatePaystackPayment } from './PaystackPayment';
import { verifySubscription } from './serverApi';
import { USD_TO_NGN } from './CoinSystem';

export const SubscriptionModal = ({ user, onClose, onSubscribe }) => {
  const [selectedType, setSelectedType] = React.useState('review');
  const [selectedProvider, setSelectedProvider] = React.useState(null); // 'flutterwave' | 'paystack'
  const [pendingPlan, setPendingPlan] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  // Step 1: User clicks "Subscribe" on a plan card → show provider picker
  const handlePlanClick = (planKey) => {
    setPendingPlan(planKey);
  };

  // Step 2: User picks Flutterwave or Paystack → trigger that checkout
  const handleProviderChoice = (provider) => {
    const plan = pendingPlan;
    const planDetails = SUBSCRIPTION_PLANS[plan];
    if (!planDetails) {
      alert('Invalid plan selected');
      return;
    }

    setSelectedProvider(provider);
    setLoading(true);

    const onPaymentSuccess = async (response) => {
      const reference = response.transaction_id || response.tx_ref || response.reference;
 
      // Server verifies the payment, activates the sub, and un-gates the author's books
      const result = await verifySubscription(reference, plan, null, provider);
 
      setLoading(false);
      setPendingPlan(null);
      setSelectedProvider(null);
 
      if (!result.success) {
        alert('Payment received but verification failed: ' + result.error +
              '\n\nContact support with reference: ' + reference);
        return;
      }
 
      // Track affiliate commission (unchanged - safe, non-money)
      if (user?.id) {
        import('./AffiliateSystem').then(({ processAffiliateCommission }) => {
          const dollarAmount = (SUBSCRIPTION_PLANS[plan]?.price || 0) / 100;
          processAffiliateCommission(
            user.id,
            plan.toLowerCase(),
            dollarAmount,
            reference
          ).catch(err => console.error('Affiliate commission error:', err));
        });
      }
 
      // Tell the app to refresh local subscription state from the server result
      onSubscribe({
        _serverActivated: true,
        plan,
        expiresAt: result.expiresAt
      });
 
      alert('🎉 Subscription successful!');
      onClose();
    };
 

    const onPaymentClose = () => {
      setLoading(false);
      setSelectedProvider(null);
      console.log('Payment cancelled by user');
    };

    if (provider === 'flutterwave') {
      initiateFlutterwavePayment(
        user.email,
        planDetails.price,
        plan,
        user.name || user.email,
        onPaymentSuccess,
        onPaymentClose
      );
    } else if (provider === 'paystack') {
      initiatePaystackPayment(
        user.email,
        planDetails.price,
        plan,
        user.name || user.email,
        onPaymentSuccess,
        onPaymentClose
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-2 sm:p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full my-4 sm:my-8">
          <div className="p-3 sm:p-6 md:p-8">
            <div className="flex justify-between items-start mb-4 sm:mb-6 sticky top-0 bg-white z-10 pb-3 border-b sm:border-0">
              <div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">
                  {pendingPlan ? 'Choose Payment Method' : 'Choose Your Plan'}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  {pendingPlan ? 'Select how you want to pay' : 'Select the best plan for your needs'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 p-1 sm:p-2 rounded-full hover:bg-gray-100 transition flex-shrink-0 ml-2"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* ── STEP 2: PROVIDER PICKER (shown after plan is selected) ── */}
            {pendingPlan ? (
              <div className="max-w-md mx-auto py-6">
                <button
                  onClick={() => setPendingPlan(null)}
                  className="text-sm text-indigo-600 mb-4 hover:underline"
                >
                  ← Back to plans
                </button>

                <div className="bg-gray-50 rounded-xl p-4 mb-6 text-center">
                  <p className="text-sm text-gray-600">Selected Plan</p>
                  <p className="font-bold text-lg text-gray-800">
                    {SUBSCRIPTION_PLANS[pendingPlan]?.name || pendingPlan}
                  </p>
                  <p className="text-2xl font-bold text-indigo-600">
                    ${(SUBSCRIPTION_PLANS[pendingPlan]?.price / 100).toFixed(2)}
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => handleProviderChoice('flutterwave')}
                    disabled={loading}
                    className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-xl hover:border-indigo-400 transition disabled:opacity-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-gray-800">Flutterwave</p>
                        <p className="text-xs text-gray-500">Card, bank transfer, USSD</p>
                      </div>
                    </div>
                    {loading && selectedProvider === 'flutterwave' && (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
                    )}
                  </button>

                  <button
                    onClick={() => handleProviderChoice('paystack')}
                    disabled={loading}
                    className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-xl hover:border-indigo-400 transition disabled:opacity-50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-gray-800">Paystack</p>
                        <p className="text-xs text-gray-500">
                          Card, bank transfer • ≈ ₦{Math.round((SUBSCRIPTION_PLANS[pendingPlan]?.price / 100) * USD_TO_NGN).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {loading && selectedProvider === 'paystack' && (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* ── PLAN TYPE (Review only — promotion/combo tabs removed) ── */}
                <h3 className="text-sm sm:text-base font-semibold text-gray-700 mb-4 sm:mb-6">
                  📚 Review Plans
                </h3>

                {/* ── PLAN CARDS ── */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(SUBSCRIPTION_PLANS)
                    .filter(([key]) => key.toUpperCase().includes(selectedType.toUpperCase()))
                    .map(([key, planDetails]) => (
                      <div key={key} className="border-2 border-gray-200 rounded-xl p-5 hover:border-indigo-400 transition">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="font-bold text-lg text-gray-800">{planDetails.name || key}</h3>
                          {planDetails.popular && (
                            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full font-semibold">
                              Popular
                            </span>
                          )}
                        </div>
                        <p className="text-3xl font-bold text-gray-800 mb-1">
                          ${(planDetails.price / 100).toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500 mb-4">{planDetails.duration} days</p>
                        {planDetails.features?.map((f, i) => (
                          <div key={i} className="flex items-start gap-2 mb-2 text-sm text-gray-700">
                            <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span>{f}</span>
                          </div>
                        ))}
                        <button
                          onClick={() => handlePlanClick(key)}
                          className="w-full mt-4 bg-indigo-600 text-white py-2.5 rounded-lg font-semibold hover:bg-indigo-700 transition"
                        >
                          Subscribe Now
                        </button>
                      </div>
                    ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};